import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import InputForms from "../../UiComponents/forms/InputForms";
import styles from "./SelectConfigCabine.module.scss";
import { normalize } from "../../../utils/fonctionSearch";

const MIN_CHARS = 2;
const MAX_RESULTS = 30;

const SelectConfigCabine = ({
  cabId,
  cabine,
  setCabId,
  isSearchingSettingCab,
  setIsSearchingSettingCab,
}) => {
  const { t } = useTranslation();
  const [searchCab, setSearchCab] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [visibleLimit, setVisibleLimit] = useState(MAX_RESULTS);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchCab), 250);
    return () => clearTimeout(timer);
  }, [searchCab]);

  // Reset on cabine change
  useEffect(() => {
    setSearchCab("");
    setDebouncedSearch("");
  }, [cabine]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsSearchingSettingCab(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsSearchingSettingCab]);

  // Reset limit and selection on search change
  useEffect(() => {
    setVisibleLimit(MAX_RESULTS);
    setSelectedIndex(-1);
  }, [debouncedSearch]);

  const searchTerms = useMemo(() =>
    normalize(debouncedSearch).split(" ").filter((t) => t.length > 0),
    [debouncedSearch]
  );

  const hasSearch = searchTerms.length > 0 && searchTerms.some((t) => t.length >= MIN_CHARS);

  const filteredCabines = useMemo(() => {
    if (!hasSearch || !Array.isArray(cabine)) return [];
    return cabine.filter((c) => {
      const fields = [normalize(c.cabname), c.id.toString()];
      return searchTerms.every((term) => fields.some((f) => f.includes(term)));
    });
  }, [hasSearch, cabine, searchTerms]);

  const selectedCabName = useMemo(() => {
    if (!cabId || !Array.isArray(cabine)) return "";
    const found = cabine.find((c) => String(c.id) === String(cabId));
    return found ? `${found.cabname} (${found.id})` : "";
  }, [cabId, cabine]);

  const handleSelect = useCallback((id) => {
    setCabId(id);
    setSearchCab("");
    setDebouncedSearch("");
    setIsSearchingSettingCab(false);
    setSelectedIndex(-1);
  }, [setCabId, setIsSearchingSettingCab]);

  const handleKeyDown = useCallback((e) => {
    if (!isSearchingSettingCab || filteredCabines.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, Math.min(filteredCabines.length, visibleLimit) - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredCabines[selectedIndex].id);
    } else if (e.key === "Escape") {
      setIsSearchingSettingCab(false);
      setSelectedIndex(-1);
    }
  }, [isSearchingSettingCab, filteredCabines, selectedIndex, visibleLimit, handleSelect, setIsSearchingSettingCab]);

  // Highlight
  const highlightMatch = (text) => {
    if (!text || searchTerms.length === 0) return text || "—";
    const normalized = normalize(text);
    const matches = [];
    for (const term of searchTerms) {
      let idx = normalized.indexOf(term);
      while (idx !== -1) {
        matches.push({ start: idx, end: idx + term.length });
        idx = normalized.indexOf(term, idx + 1);
      }
    }
    if (matches.length === 0) return text;
    matches.sort((a, b) => a.start - b.start);
    const merged = [matches[0]];
    for (let i = 1; i < matches.length; i++) {
      const prev = merged[merged.length - 1];
      if (matches[i].start <= prev.end) prev.end = Math.max(prev.end, matches[i].end);
      else merged.push(matches[i]);
    }
    const parts = [];
    let lastIndex = 0;
    for (const m of merged) {
      if (m.start > lastIndex) parts.push(text.slice(lastIndex, m.start));
      parts.push(<mark key={m.start} className={styles.highlight}>{text.slice(m.start, m.end)}</mark>);
      lastIndex = m.end;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const showDropdown = isSearchingSettingCab && searchCab.length > 0;

  return (
    <div className={styles.selectCabContainer} ref={containerRef}>
      {selectedCabName && (
        <div className={styles.selectedBadge}>
          {selectedCabName}
        </div>
      )}

      <InputForms
        value={searchCab}
        onChange={(e) => {
          setSearchCab(e.target.value);
          setIsSearchingSettingCab(true);
        }}
        onFocus={() => {
          if (searchCab.length > 0) setIsSearchingSettingCab(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={t("superadmin_ui.search_cabines_placeholder")}
        id={styles.selectCabInput}
      />

      {showDropdown && (
        <div className={styles.results}>
          {!hasSearch && (
            <div className={styles.statusMessage}>
              {t("superadmin_ui.search_min_chars", { count: MIN_CHARS })}
            </div>
          )}

          {hasSearch && (
            <>
              <div className={styles.totalCount}>
                {t("superadmin_ui.cabines_search_count", { count: filteredCabines.length })}
              </div>

              <div className={styles.resultList}>
                {filteredCabines.length === 0 ? (
                  <div className={styles.noResult}>{t("superadmin_ui.no_result")}</div>
                ) : (
                  filteredCabines.slice(0, visibleLimit).map((cab, idx) => (
                    <div
                      key={cab.id}
                      className={`${styles.resultItem} ${selectedIndex === idx ? styles.resultItemActive : ""}`}
                      onClick={() => handleSelect(cab.id)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                    >
                      <span className={styles.resultName}>
                        {highlightMatch(cab.cabname)}
                      </span>
                      <span className={styles.resultSub}>ID: {cab.id}</span>
                    </div>
                  ))
                )}
                {filteredCabines.length > visibleLimit && (
                  <button
                    type="button"
                    className={styles.moreCount}
                    onClick={() => setVisibleLimit((prev) => prev + MAX_RESULTS)}
                  >
                    +{filteredCabines.length - visibleLimit} {t("superadmin_ui.more_results", { defaultValue: "autres" })}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <select
        className={styles.selectNative}
        value={cabId}
        onChange={(e) => handleSelect(e.target.value)}
      >
        <option value="">{t("superadmin_ui.select_cabine_option")}</option>
        {Array.isArray(cabine) &&
          cabine.map((cab) => (
            <option key={cab.id} value={cab.id}>
              {cab.cabname} ({cab.id})
            </option>
          ))}
      </select>
    </div>
  );
};

export default SelectConfigCabine;
